import { useEffect, useRef, useState } from "react";
import { useApolloClient, useLazyQuery, useMutation } from "@apollo/client";
import { useLocation, useHistory } from "react-router-dom/";
import { DYNO, DYNO_REVIEW, DYNO_SUBMISSION, QSET, REQUESTSTORES, AICHAT, QUESTIONTAG } from "../../graphql/queries";
import { SENDFEEDBACKFORBADRESPONSE } from "../../graphql/mutations";
import { getDefaultValue, normalizeDynoMath } from "../../utils/helper";
import { wrapLatexInHtml } from "../../utils/latex";

export const useQViewer = ({ user, loadingCallback, isReview = false }) => {
  const [selectedDyno, setSelectedDyno] = useState({});
  const [selectedDynoId, setSelectedDynoId] = useState("");
  const [requestStores, setRequestStores] = useState({
    constants: "",
    functions: "",
    patterns: "",
    numberings: "",
    statuses: "",
    types: "",
    rubrics: "",
  });
  const [dynoIdList, setDynoIdList] = useState([]);
  const [lastAttemptedDynoId, setLastAttemptedDynoId] = useState("");
  const [qSetId, setQSetId] = useState("");
  const [dynoLoading, setDynoLoading] = useState(false);
  const [currentQSetDynoIndex, setCurrentQSetDynoIndex] = useState(false);
  const [disableNext, setDisableNext] = useState(false);
  const [disablePrev, setDisablePrev] = useState(false);
  const [isLastDynoOfQSet, setIsLastDynoOfQSet] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [chat, setChat] = useState([])
  const [toggleChat, setToggleChat] = useState(true);
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [hasReachedMaxLimit, setHasReachedMaxLimit] = useState(false);
  const [response, setResponse] = useState("");
  const [hideFirstQuestionBtn, setHideFirstQuestionBtn] = useState(false);
  const [openModal, setOpenModal] = useState(false)
  const [feedback, setFeedback] = useState("")
  const [feedbackResponse, setFeedbackResponse] = useState({})
  const [hasHiddenObjects, setHasHiddenObjects] = useState(false)
  const [qSetName, setQSetName] = useState("")
  // Learn mode: after each submit the same question is reloaded with the student's answers so the
  // viewer shows them graded, and the student moves on with "Next question" instead of auto-advancing.
  const [isRevealed, setIsRevealed] = useState(false)
  const [learnResult, setLearnResult] = useState(null)

  const history = useHistory();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const qsetid = queryParams.get("qsetid");
  const appid = queryParams.get("appid");
  const userid = queryParams.get("userid");
  const previewModeParam = (queryParams.get("preview_mode") || "").toLowerCase();
  const directModeParam = (queryParams.get("direct_mode") || queryParams.get("direct") || "").toLowerCase();
  const isPreviewMode = ["1", "true", "yes"].includes(previewModeParam);
  const isDirectMode = ["1", "true", "yes"].includes(directModeParam);
  const isLearnMode = (queryParams.get("mode") || "").toLowerCase() === "learn";
  const effectiveUserId = isPreviewMode ? (userid || user.id) : user.id;

  const closeModal = () => {
    setOpenModal(false)
  }

  const [getRequestStores] = useLazyQuery(REQUESTSTORES, {
    onCompleted: (data) => {
      setRequestStores({
        constants: { constants: data.constants },
        functions: {
          functions: data.functions.map(
            ({
              id: _id,
              name,
              example,
              space,
              description,
              arguments: params,
            }) => ({
              _id,
              name,
              example,
              space,
              description,
              arguments: params,
            }),
          ),
        },
        patterns: {
          patterns: data.patterns.map(
            ({ id: _id, patternName: name, patternCode: pattern }) => ({
              id: _id,
              name: name,
              pattern: pattern,
            }),
          ),
        },
        units: {
          units: data.numberings.map(({ id: _id, name, units }) => ({
            id: _id,
            name: name,
            units: units,
          })),
        },
        statuses: { statuses: data.dynoStatuses },
        types: { types: data.dynoTypes },
        rubrics: { rubrics: [] },
      });
    },
  });

  const [getQuestionTags] = useLazyQuery(QUESTIONTAG, {
    onCompleted: (data) => {
      setHasHiddenObjects(data.questionNode.questionTags.map(d => d.name).includes("hidden objects"))
    },
  })

  const apolloClient = useApolloClient();
  const prefetchedDynoIds = useRef(new Set());
  // The question currently on screen, as fetched, so the Learn reveal only needs to fetch the submission.
  const loadedDynoRef = useRef(null);

  // Loads a question into the Apollo cache in the background, so moving to it needs no network wait.
  // Question content doesn't change during a session; review mode is never prefetched (it needs fresh answers).
  const prefetchDyno = (dynoId) => {
    if (!dynoId || isReview || prefetchedDynoIds.current.has(dynoId)) return;
    prefetchedDynoIds.current.add(dynoId);
    apolloClient
      .query({ query: DYNO, variables: { where: { id: dynoId } }, fetchPolicy: "cache-first" })
      .catch(() => prefetchedDynoIds.current.delete(dynoId));
  };

  const onDynoLoaded = (dyno, withSubmission) => {
    if (!withSubmission) loadedDynoRef.current = dyno;
    getQuestionTags({
      variables: {
        where: {
          questionVariableId: {
            id: {
              equals: dyno.variables.find(v => v.title.includes("question")).id
            }
          }
        }
      }
    })
    if(dyno.id === dynoIdList[dynoIdList.length - 1].id) {
      setIsLastDynoOfQSet(true)
    }
    let { myDyno, myFields, myGrids, mySubmission } = getDynoData(dyno, withSubmission);
    setSelectedDyno({
      myDyno: myDyno,
      myFields: myFields,
      myGrids: myGrids,
      mySubmission: withSubmission ? mySubmission : [],
    });
    const dynoIndex = dynoIdList.findIndex(
      (lst) => lst.id === selectedDynoId,
    );
    if (dynoIndex === 0) {
      setDisablePrev(true);
    } else {
      setDisablePrev(false);
    }
    if (dynoIndex === dynoIdList.length - 1) {
      setDisableNext(true);
    } else {
      setDisableNext(false);
    }
    setCurrentQSetDynoIndex(dynoIndex + 1);
    loadingCallback(false, "");
    setDynoLoading(false);
    // While the student works on this question, fetch the next two.
    if (dynoIndex > -1) {
      prefetchDyno(dynoIdList[dynoIndex + 1]?.id);
      prefetchDyno(dynoIdList[dynoIndex + 2]?.id);
    }
  };

  // Questions are read from the cache (filled by prefetchDyno); review needs fresh submissions.
  const [getDyno] = useLazyQuery(isReview ? DYNO_REVIEW : DYNO, {
    onCompleted: (data) => onDynoLoaded(data.dyno, isReview),
    fetchPolicy: isReview ? "cache-and-network" : "cache-first",
  });

  const revealWithSubmission = (dyno) => {
    setLearnResult(getLearnResult(dyno));
    setIsRevealed(true);
    onDynoLoaded(dyno, true);
  };

  const onRevealError = () => {
    loadingCallback(false, "");
    setDynoLoading(false);
  };

  // Learn reveal: fetch just the new submission and combine it with the question already on screen.
  // network-only so it is always the submission that was just made, never a cached older one.
  const [getRevealedSubmission] = useLazyQuery(DYNO_SUBMISSION, {
    onCompleted: (data) => {
      const loadedDyno = loadedDynoRef.current;
      if (!loadedDyno || loadedDyno.id !== data.dyno?.id) {
        // Shouldn't happen, but fall back to fetching the whole question with its submission.
        getRevealedDyno({ variables: { where: { id: selectedDynoId }, submission_where: getSubmissionWhere(selectedDynoId, effectiveUserId) } });
        return;
      }
      revealWithSubmission({ ...loadedDyno, submissions: data.dyno.submissions });
    },
    onError: onRevealError,
    fetchPolicy: "network-only",
  });

  const [getRevealedDyno] = useLazyQuery(DYNO_REVIEW, {
    onCompleted: (data) => revealWithSubmission(data.dyno),
    onError: onRevealError,
    fetchPolicy: "network-only",
  });

  const [getQSet] = useLazyQuery(QSET, {
    onCompleted: (data) => {
      setQSetName(data.qset.qSetName || "");
      setDynoIdList(data.qset.dynosUnderQSet);
      if (isReview) {
        setSelectedDynoId(data.qset.dynosUnderQSet[0].id);
        setCurrentQSetDynoIndex(0 + 1);
        getSelectedDyno(data.qset.dynosUnderQSet[0].id, effectiveUserId);
        setDisablePrev(true);
        return true;
      }
      if (data.qset.qSetSubmissions.length === 0) {
        setSelectedDynoId(data.qset.dynosUnderQSet[0].id);
        setCurrentQSetDynoIndex(0 + 1);
        getSelectedDyno(data.qset.dynosUnderQSet[0].id, effectiveUserId);
        setLastAttemptedDynoId("");
        return true;
      }
      const qSetSubmissions = data.qset.qSetSubmissions[0];
      if (qSetSubmissions.isAllQuestionSubmitted) {
        setSelectedDynoId(data.qset.dynosUnderQSet[0].id);
        setCurrentQSetDynoIndex(0 + 1);
        getSelectedDyno(data.qset.dynosUnderQSet[0].id, effectiveUserId);
        setLastAttemptedDynoId("");
        return true;
      }
      const index = data.qset.dynosUnderQSet.findIndex(
        (dy) => dy.id === data.qset.qSetSubmissions[0].lastDynoAttempted.id,
      );
      setCurrentQSetDynoIndex(Number(index) + 2); //The index is for the lastDyno, so currentDyno is index+1 as index starts with 0 hence index+1+1
      setSelectedDynoId(data.qset.dynosUnderQSet[index + 1].id);
      getSelectedDyno(data.qset.dynosUnderQSet[index + 1].id, effectiveUserId);
      setLastAttemptedDynoId(data.qset.qSetSubmissions[0].lastDynoAttempted.id);
    },
    fetchPolicy: "cache-and-network",
  });

  const [sendFeedbackForBadResponse] = useMutation(SENDFEEDBACKFORBADRESPONSE, {
    onCompleted: () => {
      setFeedback("")
      setFeedbackResponse({sent: true, success: true, message: "Submitted Feedback!"})
      const chatCopy = [...chat]
      chatCopy[chat.length - 1] = {...chatCopy[chat.length - 1], like: false, dislike: true}
      setChat(chatCopy)
      localStorage.setItem("chat", JSON.stringify(chatCopy))
      setTimeout(() => {
        setOpenModal(false)
        setFeedbackResponse({})
      }, 2000)
    },
    onError: () => {
      setFeedback("")
      setFeedbackResponse({sent: true, success: false, message: "Failed To Submit Feedback!"})
      setTimeout(() => {
        setOpenModal(false)
        setFeedbackResponse({})
      }, 2000)
    }
  })

  const submitFeedback = () => {
    const ques = document?.querySelector("questionrow:nth-child(1) questioncell:nth-child(2) cellcontent")?.innerHTML
    let parser = new DOMParser();
    let doc = parser.parseFromString(ques, 'text/html');
    removeAttributes(doc.body);
    let updatedQuestionHTML = doc.body.innerHTML;
    const data = {
      sender: process.env.REACT_APP_SENDER_MAIL,
      receivers: [process.env.REACT_APP_RECEIVER_MAIL],
      user: user.id,
      name: updatedQuestionHTML,
      message: feedback,
      response: JSON.stringify(chat),
      url: window.location.href
    }
    sendFeedbackForBadResponse({
      variables: {
        data: data
      }
    })
  }

function extractFollowUpQuestions(htmlString) {
  // Create a DOM parser
  const parser = new DOMParser();

  // Parse the HTML string
  const doc = parser.parseFromString(htmlString, 'text/html');

  // Initialize result array
  let followUpQuestions = [];

  // 1. Try to find a heading or paragraph that mentions "follow-up"
  const followUpStart = Array.from(doc.querySelectorAll('p, h1, h2, h3, h4, h5, h6')).find(p =>
    p.textContent.toLowerCase().includes("follow-up")
  );

  // 2. Look for a list (ol/ul) after the follow-up heading
  let list = null;
  if (followUpStart) {
    let nextSibling = followUpStart.nextElementSibling;
    while (nextSibling) {
      if (nextSibling.tagName.toLowerCase() === 'ol' || nextSibling.tagName.toLowerCase() === 'ul') {
        list = nextSibling;
        break;
      }
      nextSibling = nextSibling.nextElementSibling;
    }
  }

  // 3. Extract questions from the list
  if (list) {
    followUpQuestions = Array.from(list.querySelectorAll('li')).map(li => li.textContent.trim());
    list.remove();
  }

  // 4. Remove the follow-up heading/paragraph if found
  if (followUpStart) {
    followUpStart.remove();
  }

  // 5. If no list was found, fallback: extract paragraphs ending with ?
  if (followUpQuestions.length === 0) {
    const questionParas = Array.from(doc.querySelectorAll('p')).filter(p =>
      p.textContent.trim().endsWith('?')
    );
    followUpQuestions = questionParas.map(p => {
      p.remove(); // Optionally remove it from DOM
      return p.textContent.trim();
    });
  }

  // 6. Rebuild the cleaned HTML string
  const newHtmlString = new XMLSerializer().serializeToString(doc);

  return {
    followUpQuestions,
    newHtmlString
  };
}

  
  const [getAIChat] = useLazyQuery(AICHAT, {
    onCompleted: (data) => {
      setChatLoading(false)
      setResponse(data.ninjaLearnerChat)
      const chatCopy = [...chat]
      const number = chat.length - 1
      // wrapLatexInHtml adds math delimiters to bare LaTeX so MathJax renders it.
      chatCopy[number] = {...chatCopy[number], question: chatCopy[number].question, response: wrapLatexInHtml(extractFollowUpQuestions(data.ninjaLearnerChat).newHtmlString), like: false, dislike: false}
      setChat(chatCopy)
      setFollowUpQuestions(extractFollowUpQuestions(data.ninjaLearnerChat).followUpQuestions)
      if(chatCopy.filter(c => !c.regenerate).length >= 4) {
        setHasReachedMaxLimit(true)
      }
    },
    onError: () => {
      setChatLoading(false)
      setChatLoading(false)
    },
    fetchPolicy: "network-only",
  });

  function removeAttributes(element, attributesToRemove = ['style', 'class', 'data-fontsize']) {
    // Remove the specified attributes from the current element
    attributesToRemove.forEach(attr => {
        if (element.hasAttribute(attr)) {
            element.removeAttribute(attr);
        }
    });

    // Recursively call the function for each child element
    Array.from(element.children).forEach(child => removeAttributes(child, attributesToRemove));
  }

  const askAI = (message, isRegenerate=false) => {
    const num = chat.length
    const chatCopy = [...chat]
    if(isRegenerate) {
      chatCopy[num] = {question: message, response: !!chatCopy[num]?.response ? chatCopy[num].response : "", regenerate: true}
    } else {
      chatCopy[num] = {question: message, response: !!chatCopy[num]?.response ? chatCopy[num].response : ""}
    }
    setChat(chatCopy)
    localStorage.setItem("chat", JSON.stringify(chatCopy))
    const ques = document?.querySelector("questionrow:nth-child(1) questioncell:nth-child(1) cellcontent").innerHTML
    let parser = new DOMParser();
    let doc = parser.parseFromString(ques, 'text/html');
    removeAttributes(doc.body);
    let updatedQuestionHTML = doc.body.innerHTML;
    setChatLoading(true)
    const img = doc.body.querySelector("span[data-type=image]")?.getAttribute("fieldid") ? doc.body.querySelector("span[data-type=image]")?.getAttribute("fieldid") : document?.querySelector("questionrow:nth-child(1) questioncell:nth-child(1) cellcontent").querySelector("span[data-type=image]")?.getAttribute("fieldid")
    if(img) {
      const questionImage = JSON.parse(selectedDyno.myFields.filter(field => field.info.name.includes(img))[0].info.v_attrs).src
      getAIChat({
        variables: {
          where: {
            message: updatedQuestionHTML,
            image: questionImage,
            isFollowUp: false,
            hasHiddenObjects: hasHiddenObjects
          }
        }
      })
    } else {
      getAIChat({
        variables: {
          where: {
            message: updatedQuestionHTML,
            isFollowUp: false,
            hasHiddenObjects: hasHiddenObjects
          }
        }
      })
    }
  }

  const askFollowUPQuestion = (followUpMessage, isRegenerate=false) => {
    const num = chat.length
    const chatCopy = [...chat]
    if(isRegenerate) {
      chatCopy[num] = {question: "Regenerate the solution", response: !!chatCopy[num]?.response ? chatCopy[num].response : "", regenerate: true}
    } else {
      chatCopy[num] = {question: followUpMessage, response: !!chatCopy[num]?.response ? chatCopy[num].response : ""}
    }
    setChat(chatCopy)
    localStorage.setItem("chat", JSON.stringify(chatCopy))
    const ques = document?.querySelector("questionrow:nth-child(1) questioncell:nth-child(1) cellcontent")?.innerHTML
    let parser = new DOMParser();
    let doc = parser.parseFromString(ques, 'text/html');
    removeAttributes(doc.body);
    let updatedQuestionHTML = doc.body.innerHTML;
    setChatLoading(true)
    const img = doc.body.querySelector("span[data-type=image]")?.getAttribute("fieldid") ? doc.body.querySelector("span[data-type=image]")?.getAttribute("fieldid") : document?.querySelector("questionrow:nth-child(1) questioncell:nth-child(1) cellcontent").querySelector("span[data-type=image]")?.getAttribute("fieldid")
    if(img) {
      const questionImage = JSON.parse(selectedDyno.myFields.filter(field => field.info.name.includes(img))[0].info.v_attrs).src
      getAIChat({
        variables: {
          where: {
            message: updatedQuestionHTML,
            image: questionImage,
            response: response,
            followUpMessage: followUpMessage,
            isFollowUp: true,
          }
        }
      })
    } else {
      getAIChat({
        variables: {
          where: {
            message: updatedQuestionHTML,
            response: response,
            followUpMessage: followUpMessage,
            isFollowUp: true,
          }
        }
      })
    }
  }

  const regenerate = (message) => {
    if(message === "Explain the solution step by step") {
      askAI("Regenerate the solution", true)
    } else {
      askFollowUPQuestion(message, true)
    }
  }

  const toggleAIChat = () => {
    setToggleChat(!toggleChat)
  }

  const askFirstQuestion = () => {
    askAI("Explain the solution step by step", 0)
    if(!hideFirstQuestionBtn) setToggleChat(false)
    setHideFirstQuestionBtn(true)
  }

  const onReview = (like, dislike, index) => {
    if(!dislike) {
      const chatCopy = [...chat]
      chatCopy[index] = {...chatCopy[index], like, dislike}
      setChat(chatCopy)
      localStorage.setItem("chat", JSON.stringify(chatCopy))
    } else {
      setOpenModal(true)
    }
  }

  useEffect(() => {
    getRequestStores();
    loadingCallback(true, "");
    const c = localStorage.getItem("chat")
    const f = localStorage.getItem("followupQuestion")
    
    if(c) setChat(JSON.parse(c))
    if(c && c.length > 0) setHideFirstQuestionBtn(true)
    if(f) setFollowUpQuestions(f >= 4 ? f : JSON.parse(f))
    
  }, [getRequestStores, loadingCallback]);

  useEffect(() => {
    const url = new URL(window.location);
    const qSet = url.searchParams.get("qsetid");
    setQSetId(qSet);
    if(effectiveUserId) {
      getQSet({
        variables: {
          where: {
            id: qSet,
          },
        },
      });
    }
  }, [effectiveUserId, getQSet]);

  // The embedded viewer sets the tab title to each dyno's own name (e.g. "Dimension Analysis Review - Q.2")
  // through react-helmet on every question change. Keep the tab on the QSet name instead. The dyno's
  // info.name is left alone because the viewer's save paths write it back as the dyno title.
  useEffect(() => {
    if (!qSetName) return;
    const previousTitle = document.title;
    const applyTitle = () => {
      if (document.title !== qSetName) document.title = qSetName;
    };
    applyTitle();
    const observer = new MutationObserver(applyTitle);
    observer.observe(document.head, { subtree: true, childList: true, characterData: true });
    return () => {
      observer.disconnect();
      document.title = previousTitle;
    };
  }, [qSetName]);

  useEffect(()=>{
    if(typeof window?.MathJax !== "undefined"){
      window.MathJax.typeset()
    }
  },[])

  const getSubmissionWhere = (dynoId, userId) => ({
    AND: [
      {
        submitter: {
          id: { equals: userId },
        },
        dyno: {
          id: { equals: dynoId },
        },
      },
    ],
  });

  const getSelectedDyno = (dynoId, userId) => {
    getDyno({
      variables: {
        where: {
          id: dynoId,
        },
        ...(isReview ? {
          submission_where: getSubmissionWhere(dynoId, userId),
        }: {})
      },
    });
  };

  // Points earned vs. points available on the latest submission. Falls back to the best possible answer
  // per response field when the dyno has no maxPoints set.
  const getLearnResult = (gqlDyno) => {
    const submission = gqlDyno.submissions?.[0];
    if (!submission) return null;
    const earned = (submission.quizAnswers || []).reduce((acc, qa) => acc + (Number(qa.points) || 0), 0);
    const fieldsMax = (gqlDyno.submissionsVariables || []).reduce((acc, variable) => {
      const best = Math.max(0, ...(variable.possibleAnswers || []).map((pa) => Number(pa.maxPoints) || 0));
      return acc + best;
    }, 0);
    const max = Number(gqlDyno.maxPoints) || fieldsMax;
    return { earned, max };
  };

  const getDynoData = (fetchedDyno, withSubmission) => {
    const gqlDyno = normalizeDynoMath(fetchedDyno);
    let myDyno = {
      _id: gqlDyno.id,
      owner_id: gqlDyno.owner && gqlDyno.owner.id,
      owner_email: gqlDyno.owner && gqlDyno.owner.email,
      function_ids:
        gqlDyno.functions &&
        gqlDyno.functions.map(function (_ref9) {
          var id = _ref9.id;
          return id;
        }),
      updatedAt: gqlDyno.updatedAt,
      info: {
        name: gqlDyno.dynoTitle,
        description: gqlDyno.description,
        html_code: gqlDyno.dynoContent,
        category: gqlDyno.category && gqlDyno.category.id,
        dyno_type: gqlDyno.dynoType && gqlDyno.dynoType.name,
        dyno_type_id: gqlDyno.dynoType && gqlDyno.dynoType.id,
        is_public:
          gqlDyno.accessType === "ANYONEWITHLINK" ||
          gqlDyno.accessType === "PUBLIC",
        is_draft: gqlDyno.status && gqlDyno.status.statusType === "Draft",
        allow_rating: gqlDyno.allowRating,
        allow_comments: gqlDyno.allowComments,
        tags:
          gqlDyno.tags &&
          gqlDyno.tags.map(function (_ref10) {
            var id = _ref10.id;
            return id;
          }),
        locale_date: gqlDyno.localeDate ? gqlDyno.localeDate.name : null,
        locale: gqlDyno.locale ? gqlDyno.locale.name : null,
        maxPoints: gqlDyno.maxPoints,
        instructions: gqlDyno.instructions,
        dueDate: gqlDyno.dueDate,
        isTimedDyno: gqlDyno.isTimedDyno,
        timedDynoDuration: gqlDyno.timedDynoDuration,
        viewCount: gqlDyno.viewCount,
      },
      parentDyno: gqlDyno.parentDyno,
      isStudentDyno: false,
      rubric: gqlDyno.rubric,
      submission_id:
        gqlDyno.submissions?.length > 0 ? gqlDyno.submissions[0].id : "",
      submission_variables: gqlDyno.submissionsVariables,
      submission_variables_db: JSON.parse(
        JSON.stringify(gqlDyno.submissionsVariables),
      ),
      lmsId: gqlDyno.lmsId,
      otherInfo: gqlDyno.otherInfo,
    };
    let myFields = [];

    if (myDyno) {
      if (gqlDyno.variables.length) {
        myFields = gqlDyno.variables.map(function (variable) {
          return {
            _id: variable.id,
            info: {
              name: variable.fieldName,
              description: variable.description,
              v_type:
                variable.variableType && variable.variableType.variableType,
              v_default: {
                value: withSubmission ? getDefaultValue(gqlDyno.submissions[0], variable) : variable.defaultValue,
              },
              v_attrs: variable.variableAttributes
                ? JSON.stringify(variable.variableAttributes)
                : "{}",
              rules: variable.variableRules,
            },
            possibleAnswers: variable.possibleAnswers,
          };
        });
      }
    }
    let myGrids = [];
    if (myDyno) {
      if (gqlDyno.grids.length) {
        myGrids = gqlDyno.grids;
      }
    }
    let mySubmission = withSubmission ? gqlDyno.submissions[0] : {};
    return { myDyno, myFields, myGrids, mySubmission };
  };

  const navigateToReport = () => {
    history.push(
      `/qreport?appid=${appid}&userid=${userid}&qreportid=${qsetid}${isPreviewMode ? "&preview_mode=1" : ""}${isDirectMode ? "&direct_mode=1" : ""}`,
    );
  };

  const goToNextDyno = (dynoIndex) => {
    setDynoLoading(true);
    setSelectedDynoId(dynoIdList[dynoIndex + 1].id);
    getSelectedDyno(dynoIdList[dynoIndex + 1].id, effectiveUserId);
  };

  const submissionCallback = (submission) => {
    setLastAttemptedDynoId(submission.data.upsertQSetDynoSubmission.submission.dyno.id);
    const dynoIndex = dynoIdList.findIndex((lst) => lst.id === selectedDynoId);
    const isLastQuestion = dynoIndex + 1 === dynoIdList.length;
    if (isLastQuestion && submission.data.upsertQSetDynoSubmission?.newBadgeUnlocked !== null) {
      const data = {
        badgeName: submission.data.upsertQSetDynoSubmission?.newBadgeUnlocked?.badgeName,
        badgeImage: submission.data.upsertQSetDynoSubmission?.newBadgeUnlocked?.badgeImage,
      }
      localStorage.setItem('badge', JSON.stringify(data))
    }
    if (isLearnMode) {
      // Also cleared here (not only on Next) to drop a chat left in localStorage by an earlier review session.
      resetLearnChat()
      setDynoLoading(true);
      getRevealedSubmission({
        variables: {
          where: { id: selectedDynoId },
          submission_where: getSubmissionWhere(selectedDynoId, effectiveUserId),
        },
      });
      return;
    }
    if (isLastQuestion) {
      setTimeout(navigateToReport, 1000)
    } else {
      goToNextDyno(dynoIndex);
    }
  };

  // Each Learn question gets a fresh Ninja chat, never one left over from the previous question.
  const resetLearnChat = () => {
    localStorage.removeItem("chat")
    localStorage.removeItem("followupQuestion")
    setChat([])
    setFollowUpQuestions([])
    setHideFirstQuestionBtn(false)
    setHasReachedMaxLimit(false)
    setToggleChat(true)
  };

  // Learn mode only: leave the revealed answer and continue with the next question (or the report).
  const goToNextLearnQuestion = () => {
    resetLearnChat()
    setIsRevealed(false)
    setLearnResult(null)
    const dynoIndex = dynoIdList.findIndex((lst) => lst.id === selectedDynoId);
    if (dynoIndex + 1 === dynoIdList.length) {
      navigateToReport();
    } else {
      goToNextDyno(dynoIndex);
    }
  };

  const onChangeDyno = (isNext) => {
    localStorage.clear()
    setFollowUpQuestions([])
    setHideFirstQuestionBtn(false)
    setChat([])
    setToggleChat(true)
    const dynoIndex = dynoIdList.findIndex((lst) => lst.id === selectedDynoId);
    if (isNext) {
      const fromPathN = sessionStorage.getItem("fromPathN")
      sessionStorage.setItem("fromPathN", Number(fromPathN) + 1)
      if (dynoIndex + 1 === dynoIdList.length) {
        setDisableNext(true);
      } else {
        setDisableNext(false);
        setDynoLoading(true);
        setSelectedDynoId(dynoIdList[dynoIndex + 1].id);
        getSelectedDyno(dynoIdList[dynoIndex + 1].id, effectiveUserId);
      }
    } else {
      const fromPathN = sessionStorage.getItem("fromPathN")
      sessionStorage.setItem("fromPathN", Number(fromPathN) - 1)
      if (dynoIndex - 1 === -1) {
        setDisablePrev(true);
        sessionStorage.removeItem("fromPathN")
      } else {
        setDisablePrev(false);
        setDynoLoading(true);
        setSelectedDynoId(dynoIdList[dynoIndex - 1].id);
        getSelectedDyno(dynoIdList[dynoIndex - 1].id, effectiveUserId);
      }
    }
  };

  const navigateToHome = () => {
    history.replace(
      `/?appid=${appid}&userid=${userid}${isPreviewMode ? "&preview_mode=1" : ""}${isDirectMode ? "&direct_mode=1" : ""}`
    )
  }

  return {
    selectedDyno,
    effectiveUserId,
    qSetId,
    requestStores,
    lastAttemptedDynoId,
    submissionCallback,
    dynoLoading,
    currentQSetDynoIndex,
    onChangeDyno,
    navigateToHome,
    disableNext,
    disablePrev,
    isLastDynoOfQSet,
    askAI,
    chatLoading,
    toggleAIChat,
    toggleChat,
    followUpQuestions,
    chat,
    askFollowUPQuestion,
    askFirstQuestion,
    hideFirstQuestionBtn,
    onReview,
    regenerate,
    openModal,
    closeModal,
    setFeedback,
    feedback,
    submitFeedback,
    feedbackResponse,
    hasReachedMaxLimit,
    dynoIdList,
    isLearnMode,
    isRevealed,
    learnResult,
    goToNextLearnQuestion,
  };
};
